import { Component, OnInit, inject, computed, effect, input, output, signal, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { ToastModule } from 'primeng/toast';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ButtonModule } from 'primeng/button';
import {
  ProfessionalsDataService,
  TipologiaFiduciario,
  TipologiaCarrozzeria,
} from '../../../services/professionals-data.service';
import { ProfessionalsWizardService } from '../../../services/professionals-wizard.service';
import { UserSessionService } from '../../../../core/services/mock-auth.service';
import { ProfessionalsService } from '../../../../core/services/professionals.service';
import { RegistrationStepService, RegistrationStepResponse, RegistrationStatus, RegistrationStep } from '../../../../core/services/registration-step.service';
import { forkJoin } from 'rxjs';
import { ComponentCanDeactivate } from '../../../../core/guards/unsaved-changes.guard';
import { ConfirmationDialogService } from '../../../../core/services/confirmation-dialog.service';
import { FormDropdownService } from '../../../../core/services/form-dropdown.service';
import { AutoExpertFormComponent } from './forms/auto-expert-form.component';
import { PeritoREFormComponent } from './forms/perito-re-form.component';
import { InvestigatorFormComponent } from './forms/investigator-form.component';
import { DoctorFormComponent } from './forms/doctor-form.component';
import { LawyerFormComponent } from './forms/lawyer-form.component';
import { BodyshopFormComponent } from './forms/bodyshop-form.component';
import { HailProviderFormComponent } from './forms/hail-provider-form.component';
import { ValidationErrorSummaryComponent } from '../../validation-error-summary/validation-error-summary.component';
import { ValidationErrorSection } from '../../validation-error-summary/validation-error-summary.model';

@Component({
  selector: 'app-professionals-specific-data',
  standalone: true,
  providers: [MessageService, ConfirmationService],
  imports: [
    CommonModule,
    ToastModule,
    ConfirmDialogModule,
    ButtonModule,
    AutoExpertFormComponent,
    PeritoREFormComponent,
    InvestigatorFormComponent,
    DoctorFormComponent,
    LawyerFormComponent,
    BodyshopFormComponent,
    HailProviderFormComponent,
    ValidationErrorSummaryComponent,
  ],
  templateUrl: './professionals-specific-data.component.html',
  styleUrl: './professionals-specific-data.component.scss',
})
export class ProfessionalsSpecificDataComponent implements OnInit, ComponentCanDeactivate {
  private professionalsDataService = inject(ProfessionalsDataService);
  private professionalsWizardService = inject(ProfessionalsWizardService);
  private mockAuthService = inject(UserSessionService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private professionalsService = inject(ProfessionalsService);
  private formDropdownService = inject(FormDropdownService);
  private registrationStepService = inject(RegistrationStepService);
  private confirmationService = inject(ConfirmationService);
  private confirmDialogService = inject(ConfirmationDialogService);
  private readonly UNSAVED_CHANGES_MESSAGE = 'Se cambi sezione o lasci la pagina senza cliccare su "Salva bozza", perderai i dati inseriti. Vuoi procedere?';
  private allowNextNavigation = false;

  // Optional professional ID from parent component (for review page)
  professionalIdInput = input<string | null>(null);

  // Optional tipologia passed directly by the parent (review page) to skip cache/service lookup
  tipologiaInput = input<string | null>(null);

  // Optional input to force readonly mode (for review/detail pages)
  readonly = input<boolean>(false);

  // Optional input to force editable mode (for BO data entry on NET- body shops)
  forceEditable = input<boolean>(false);

  // Emits after a successful save (used by NetworkBodyShopOnboardingComponent)
  dataSaved = output<void>();
  validationErrorSections = signal<ValidationErrorSection[]>([]);

  handleValidationFailed(sections: ValidationErrorSection[]): void {
    this.validationErrorSections.set(sections);
  }

  closeValidationSummary(): void {
    this.validationErrorSections.set([]);
  }
  
  // Current registration step (for detecting modification requests)
  currentRegistrationStep = signal<RegistrationStepResponse | null>(null);
  
  // Step history (to check for MODIFICATION_REQUESTED)
  stepHistory = signal<RegistrationStepResponse[]>([]);

  // Computed: Check if there's a modification request
  hasModificationRequest = computed(() => {
    const step = this.currentRegistrationStep();
    const history = this.stepHistory();
    
    if (!step || this.professionalIdInput()) return false; // Don't show in review mode
    
    // Only show banner if:
    // 1. Current step is DATA_INPUT with USER_INPUT status
    // 2. There's a VERIFICATION step with MODIFICATION_REQUESTED in history
    if (step.step === 'DATA_INPUT' && step.status === RegistrationStatus.USER_INPUT) {
      return history.some(s => 
        s.step === 'VERIFICATION' && 
        s.status === RegistrationStatus.MODIFICATION_REQUESTED
      );
    }
    
    return false;
  });
  
  // Computed: Check if all fields should be disabled (readonly mode or review mode)
  isFieldsDisabled = computed(() => {
    if (this.forceEditable()) return false;
    return this.readonly() || this.professionalIdInput() !== null;
  });

  // Computed: Get modification comment from VERIFICATION MODIFICATION_REQUESTED step
  modificationComment = computed(() => {
    const history = this.stepHistory();
    
    // Find the most recent VERIFICATION with MODIFICATION_REQUESTED
    const modificationSteps = history
      .filter(s => s.step === 'VERIFICATION' && s.status === RegistrationStatus.MODIFICATION_REQUESTED)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    
    if (modificationSteps.length > 0) {
      // Parse "Modification requested: actual message" format
      const comment = modificationSteps[0].comments || '';
      return comment.replace(/^Modification requested:\s*/, '').trim();
    }
    
    return null;
  });

  // Signal for expand/collapse state of modification notes
  isModificationNotesExpanded = signal<boolean>(false);
  private readonly MAX_COMMENT_LENGTH = 150;

  // Computed: Check if comment is long
  isCommentLong = computed(() => {
    const comment = this.modificationComment();
    return comment ? comment.length > this.MAX_COMMENT_LENGTH : false;
  });

  // Computed: Get truncated comment
  displayedComment = computed(() => {
    const comment = this.modificationComment();
    if (!comment) return null;
    
    if (this.isCommentLong() && !this.isModificationNotesExpanded()) {
      return comment.substring(0, this.MAX_COMMENT_LENGTH) + '...';
    }
    
    return comment;
  });

  // Toggle modification notes expansion
  toggleModificationNotes(): void {
    this.isModificationNotesExpanded.update(expanded => !expanded);
  }
  
  /**
   * Get current professional ID.
   * Priority: explicit input → route param (:id) → network onboarding context → demo session (frontoffice user)
   */
  protected getCurrentProfessionalId(): string | null {
    if (this.professionalIdInput()) return this.professionalIdInput();
    const routeId = this.route.snapshot.paramMap.get('id');
    if (routeId) return routeId;
    if (this.professionalsWizardService.isNetworkOnboarding()) {
      return this.professionalsWizardService.networkOnboardingBodyShopId() || null;
    }
    return this.mockAuthService.getDemoProfessionalId();
  }

  tipologia = signal<TipologiaFiduciario | null>(null);
  tipologiaCarrozzeria = signal<TipologiaCarrozzeria | null>(null);
  private professionalSummaryTypeLabel = signal<string>('');
  isLoadingTipologia = signal<boolean>(false);

  // NK_BODYSHOP status (populated in initNetworkBodyShopOnboarding)
  bodyShopStatus = signal<string>('');
  isSuspendingOrReactivating = signal<boolean>(false);

  isBodyShopActive = computed(() => this.bodyShopStatus() === 'ACTIVE');
  isBodyShopSuspended = computed(() => this.bodyShopStatus() === 'SUSPENDED');

  // True when navigated directly to /axa/professionals-specific-data/NK-XXX
  isNKRoute = computed(() => {
    const routeId = this.route.snapshot.paramMap.get('id');
    return routeId?.startsWith('NK-') ?? false;
  });

  constructor() {
    // Effect: React when general data cache is populated.
    // ALL mutations to plain (non-signal) properties are deferred via
    // queueMicrotask to avoid NG0100: effects are flushed by the Angular CD
    // scheduler AFTER the template has already been evaluated in the same
    // tick, so any synchronous mutation to template-read plain properties
    // causes ExpressionChangedAfterItHasBeenCheckedError.
    effect(() => {
      const cachedData = this.professionalsWizardService.cachedGeneralData();
      
      if (cachedData?.professionalType && !this.tipologia()) {
        const tipologiaFiduciario = cachedData.professionalType as TipologiaFiduciario;
        const tipologiaCarrozzeria = (cachedData.professionalSubType as TipologiaCarrozzeria | null) || null;

        queueMicrotask(() => {
          // If we are on an NK- route, ngOnInit already handles everything via
          // initNetworkBodyShopOnboarding(). Stale cache from a previous session
          // (e.g. professionalType: 'NETWORK') must not trigger a navigation here.
          const routeId = this.route.snapshot.paramMap.get('id');
          if (routeId?.startsWith('NK-')) {
            console.log('ℹ️ [Specific Data] Effect: NK- route detected — skipping cache-driven init');
            return;
          }

          console.log('✅ [Specific Data] Using cached general data for tipologia:', tipologiaFiduciario);

          // Initialize service with cached tipologia
          this.professionalsDataService.initializeProfessionalsData(
            tipologiaFiduciario,
            tipologiaCarrozzeria ?? undefined
          );

          // Update signals → Angular CD runs automatically
          this.tipologia.set(tipologiaFiduciario);
          this.tipologiaCarrozzeria.set(tipologiaCarrozzeria);
          this.isLoadingTipologia.set(false);

          // NETWORK type has no specific data: redirect to questionnaires (wizard mode only)
          if (tipologiaFiduciario === 'NETWORK' && !this.professionalIdInput()) {
            const questionnaireStep = this.professionalsWizardService.steps().find(s => s.id === 2);
            this.router.navigate([questionnaireStep?.routerLink ?? '/axa/professionals-questionnaire-attachments']);
            return;
          }

          // Continue with BODYSHOP check if needed
          this.handleBodyshopSubtypeCheck();
        });
      }
    });

    effect(() => {
      const professionalType = this.tipologiaInput() || this.tipologia();

      if (!professionalType) {
        this.professionalSummaryTypeLabel.set('');
        return;
      }

      this.formDropdownService.decodeProfessionalType(professionalType).subscribe(label => {
        if ((this.tipologiaInput() || this.tipologia()) === professionalType) {
          this.professionalSummaryTypeLabel.set(label);
        }
      });
    });
  }

  // Computed: verifica se lo step corrente è completato
  isStepCompleted = computed(() => {
    const currentStep = this.professionalsWizardService.steps().find(step => step.id === 1);
    return currentStep?.completed ?? false;
  });

  // Computed: verifica se siamo in modalità read-only
  isReadOnly = computed(() => {
    if (this.forceEditable()) return false;
    // NK_BODYSHOP onboarding is always editable — but only in wizard mode (not embedded in review).
    if (this.professionalsWizardService.isNetworkOnboarding()) return false;
    const routeId = this.route.snapshot.paramMap.get('id');
    if (routeId?.startsWith('NK-') && !this.professionalIdInput()) return false;
    return this.professionalsDataService.isReadOnly() || this.readonly() || this.professionalIdInput() !== null;
  });

  professionalSummaryBusinessName = computed(() => {
    const generalData = this.professionalsWizardService.cachedGeneralData();
    return generalData?.companyData?.businessName || generalData?.businessName || '';
  });

  professionalSummaryType = computed(() =>
    this.professionalSummaryTypeLabel() || this.tipologiaInput() || this.tipologia() || ''
  );

  professionalIdDisplay = computed(() =>
    this.professionalIdInput() || this.mockAuthService.getDemoProfessionalId() || ''
  );

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.shouldWarnBeforeLeaving()) {
      event.preventDefault();
      event.returnValue = true;
    }
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (this.professionalsWizardService.consumeNavigationWithoutWarning()) {
      return true;
    }

    if (this.allowNextNavigation) {
      this.allowNextNavigation = false;
      return true;
    }

    if (!this.shouldWarnBeforeLeaving()) {
      return true;
    }

    return new Observable<boolean>((observer) => {
      this.confirmDialogService.confirmAction(
        this.UNSAVED_CHANGES_MESSAGE,
        () => {
          observer.next(true);
          observer.complete();
        },
        'Attenzione',
        'Esci senza salvare',
        'Resta nella pagina'
      );
    });
  }

  private shouldWarnBeforeLeaving(): boolean {
    return !this.isReadOnly();
  }

  private bypassUnsavedChangesWarningForNextNavigation(): void {
    this.allowNextNavigation = true;
  }

  ngOnInit() {
    console.log('🔵 [Specific Data] Component initialized');

    // Check if we have an NK- ID in the route (NK_BODYSHOP onboarding from BO)
    // Skip if professionalIdInput is set: we are embedded in review-professional,
    // NOT navigated directly to /axa/professionals-specific-data/NK-XXX.
    const routeId = this.route.snapshot.paramMap.get('id');
    if (routeId?.startsWith('NK-') && !this.professionalIdInput()) {
      // Synchronously prepare NK step visibility BEFORE the async HTTP call so the navbar
      // already shows [Dati Specifici, Questionari e Allegati] while the request is in-flight.
      this.professionalsWizardService.prepareNKStepVisibility(routeId);
      this.initNetworkBodyShopOnboarding(routeId);
      return;
    }

    // ── NK_BODYSHOP onboarding: wizard service already initialized (fallback if no route param) ──
    if (this.professionalsWizardService.isNetworkOnboarding()) {
      const cached = this.professionalsWizardService.cachedGeneralData();
      if (cached?.professionalType) {
        const tipologiaFiduciario = cached.professionalType as TipologiaFiduciario;
        const tipologiaCarrozzeria = (cached.professionalSubType as TipologiaCarrozzeria | null) || null;
        this.professionalsDataService.initializeProfessionalsData(tipologiaFiduciario, tipologiaCarrozzeria ?? undefined);
        this.tipologia.set(tipologiaFiduciario);
        this.tipologiaCarrozzeria.set(tipologiaCarrozzeria);
        queueMicrotask(() => this.professionalsWizardService.setStep(1));
      }
      return;
    }
    // ── end NK_BODYSHOP bypass ──

    // Load current registration step from backend (needed for read-only mode detection)
    const professionalId = this.getCurrentProfessionalId();
    if (professionalId) {
      this.professionalsWizardService.loadCurrentStep(professionalId);
      
      // Load both current step AND history to detect modification requests
      forkJoin({
        currentStep: this.registrationStepService.getCurrentStep(professionalId),
        history: this.registrationStepService.getStepHistory(professionalId)
      }).subscribe({
        next: ({ currentStep, history }) => {
          this.currentRegistrationStep.set(currentStep);
          this.stepHistory.set(history || []);
          console.log('📋 [Specific Data] Current registration step loaded:', currentStep);
          console.log('📋 [Specific Data] Step history loaded:', history?.length || 0, 'steps');
        },
        error: (err) => {
          console.error('❌ Error loading registration step or history:', err);
        }
      });
    }
    
    // Set this step as active in the wizard.
    // Deferred via queueMicrotask to avoid NG0100: mutating shared signals
    // (currentStep, steps) synchronously in ngOnInit causes
    // ExpressionChangedAfterItHasBeenCheckedError because the parent template
    // has already read those signals in the same CD cycle.
    queueMicrotask(() => this.professionalsWizardService.setStep(1));

    // Restore step 2 label to the non-NK value in case a previous NK session
    // had changed it to 'Allegati' (the wizard service is a singleton).
    this.professionalsWizardService.updateStepLabel(2, 'Questionari e Allegati');

    // Load completion status for other steps
    if (professionalId) {
      console.log('🔍 [Specific Data] Loading completion status for other steps...');
      
      // Load step 0 (general data) completion status
      this.professionalsWizardService.preloadGeneralDataStatus(professionalId);
      // NOTE: do NOT call preloadQuestionnaireStatus here — same reason as in general-data:
      // it overwrites in-memory/sessionStorage step 2 completion with a fresh BE fetch
      // that returns false when the backend stepStatus is not yet COMPLETED.
    }
    
    // --- Tipologia resolution (priority order) ---
    // 1. tipologiaInput passed directly by the parent (review mode) — no cache/API needed
    if (this.tipologiaInput()) {
      const tipologiaFiduciario = this.tipologiaInput() as TipologiaFiduciario;
      console.log('✅ [Specific Data] Using tipologiaInput from parent:', tipologiaFiduciario);
      console.log('🔍 [Specific Data] professionalIdInput:', this.professionalIdInput());
      this.professionalsDataService.initializeProfessionalsData(tipologiaFiduciario, undefined);
      this.tipologia.set(tipologiaFiduciario);
      this.handleBodyshopSubtypeCheck();
      return;
    }

    // 2. From professionalsDataService (set when user navigated through wizard)
    this.tipologia.set(this.professionalsDataService.getTipologiaFiduciario());
    this.tipologiaCarrozzeria.set(this.professionalsDataService.getTipologiaCarrozzeria());

    // NETWORK type has no specific data section: redirect to questionnaires (wizard mode only)
    if (this.tipologia() === 'NETWORK' && !this.professionalIdInput()) {
      const questionnaireStep = this.professionalsWizardService.steps().find(s => s.id === 2);
      this.router.navigate([questionnaireStep?.routerLink ?? '/axa/professionals-questionnaire-attachments']);
      return;
    }

    console.log('🔍 [Specific Data] Tipologia from service:', this.tipologia());
    console.log('🔍 [Specific Data] Tipologia Carrozzeria from service:', this.tipologiaCarrozzeria());

    // 3. From wizard cache
    if (!this.tipologia() && professionalId) {
      const cachedData = this.professionalsWizardService.cachedGeneralData();

      if (cachedData?.professionalType) {
        console.log('✅ [Specific Data] Using cached general data');
        const tipologiaFiduciario = cachedData.professionalType as TipologiaFiduciario;
        const tipologiaCarrozzeria = cachedData.professionalSubType || null;
        this.professionalsDataService.initializeProfessionalsData(tipologiaFiduciario, tipologiaCarrozzeria);
        this.tipologia.set(tipologiaFiduciario);
        this.tipologiaCarrozzeria.set(tipologiaCarrozzeria);
      } else {
        const isLoading = this.professionalsWizardService.isLoadingGeneralData(professionalId);
        if (isLoading) {
          console.log('⏳ [Specific Data] Waiting for general data to load...');
          this.isLoadingTipologia.set(true);
          return;
        } else {
          console.error('❌ [Specific Data] No general data available');
          this.redirectToGeneralData();
          return;
        }
      }
    }

    if (this.tipologia()) {
      this.handleBodyshopSubtypeCheck();
    }
  }

  /**
   * NK_BODYSHOP onboarding entry point.
   * Called when the route has an :id param starting with 'NK-'.
   * Calls GET /body-shops/{id} to get bodyShopType, then initialises
   * the wizard service and loads the correct form — no registration-step APIs.
   */
  private initNetworkBodyShopOnboarding(bodyShopId: string): void {
    this.isLoadingTipologia.set(true);
    this.professionalsService.getBodyshopData(bodyShopId).subscribe({
      next: (res) => {
        this.isLoadingTipologia.set(false);
        if (!res?.data) {
          this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Carrozzeria non trovata.', life: 5000 });
          return;
        }
        const bodyShop = res.data;
        const validSubtypes = ['STANDARD', 'ARS', 'RCI', 'NOBIS', 'AXA_DEUTSCHE_BANK'];
        const rawSubType = bodyShop.bodyShopType ?? '';
        const subType = (validSubtypes.includes(rawSubType) ? rawSubType : 'STANDARD') as TipologiaCarrozzeria;
        const networkId: string = bodyShop.networkId ?? '';
        const status: string = bodyShop.status ?? bodyShop.businessStatus ?? '';
        const specificDataSectionStatus: string = bodyShop.specificDataSectionStatus ?? '';

        // Initialise wizard service (hides steps 0 & 3, stores bodyShopId + networkId + status)
        this.professionalsWizardService.initNetworkOnboarding(bodyShopId, networkId, subType, status);

        // Mark step 1 completed only if specific data was already filled in BE
        this.professionalsWizardService.markStepCompleted(1, specificDataSectionStatus === 'FILLED');

        // Preload step 2 (Allegati) completion status from the checklist API.
        // Needed on refresh: in-memory wizard state is lost, nobody else reloads it here.
        this.professionalsWizardService.preloadNKAttachmentsStatus(bodyShopId);

        // Store body shop status for suspend/reactivate/delete actions
        this.bodyShopStatus.set(status);

        // Init data service and local signals for the form
        this.professionalsDataService.initializeProfessionalsData('NK_BODYSHOP', subType);
        this.tipologia.set('NK_BODYSHOP');
        this.tipologiaCarrozzeria.set(subType);

        queueMicrotask(() => this.professionalsWizardService.setStep(1));
      },
      error: () => {
        this.isLoadingTipologia.set(false);
        this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Impossibile caricare i dati della carrozzeria.', life: 5000 });
      },
    });
  }

  private redirectToGeneralData(): void {
    this.messageService.add({
      severity: 'warn',
      summary: 'Attenzione',
      detail: 'Completa prima i Dati Generali per procedere',
      life: 5000,
    });
    setTimeout(() => {
      this.bypassUnsavedChangesWarningForNextNavigation();
      this.router.navigate(['/axa/professionals-general-data']).then(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }, 2000);
  }

  private handleBodyshopSubtypeCheck(): void {
    // For NK_BODYSHOP onboarding the subtype comes from getBodyshopData (/body-shops/{id}),
    // already loaded in initNetworkBodyShopOnboarding(). Nothing more to fetch here.
    if (this.professionalsWizardService.isNetworkOnboarding()) {
      console.log('ℹ️ [Specific Data] NK_BODYSHOP onboarding — skipping bodyshop subtype API fetch');
      return;
    }
    // Also skip for NK- route IDs (onboarding initialising asynchronously)
    // BUT only when NOT embedded in review mode (professionalIdInput set)
    const routeId = this.route.snapshot.paramMap.get('id');
    if (routeId?.startsWith('NK-') && !this.professionalIdInput()) {
      console.log('ℹ️ [Specific Data] NK- route ID detected (wizard mode) — skipping bodyshop subtype API fetch');
      return;
    }
    // For BODYSHOP / NK_BODYSHOP, verify or fetch tipologiaCarrozzeria
    if (this.tipologia() === 'BODYSHOP' || this.tipologia() === 'NK_BODYSHOP') {
      console.log('🔧 [Specific Data] BODYSHOP/NK_BODYSHOP detected, checking tipologiaCarrozzeria...');
      const validSubtypes = ['STANDARD', 'ARS', 'RCI', 'NOBIS', 'AXA_DEUTSCHE_BANK'];
      const isValid = validSubtypes.includes(this.tipologiaCarrozzeria() || '');

      // NK_BODYSHOP defaults to STANDARD when subtype is not specified
      if (!isValid && this.tipologia() === 'NK_BODYSHOP' && !this.getCurrentProfessionalId()) {
        this.tipologiaCarrozzeria.set('STANDARD');
        this.professionalsDataService.initializeProfessionalsData('NK_BODYSHOP', 'STANDARD');
        return;
      }
      console.log('🔧 [Specific Data] Is tipologiaCarrozzeria valid?', isValid);
      console.log('🔧 [Specific Data] Valid subtypes:', validSubtypes);
      
      // If not valid, try to fetch from bodyshop API
      if (!isValid) {
        console.warn('⚠️ [Specific Data] Invalid or missing tipologiaCarrozzeria, fetching from bodyshop API...');
        const professionalId = this.getCurrentProfessionalId();
        console.log('🔍 [Specific Data] professionalId for bodyshop fetch:', professionalId);
        
        if (professionalId) {
          this.professionalsService.getBodyshopData(professionalId).subscribe({
            next: (response) => {
              console.log('🚗 [Specific Data] Raw bodyshop API response:', JSON.stringify(response));
              if (response?.data) {
                let bodyshopSubType = response.data.bodyShopType;
                console.log('🚗 [Specific Data] BODYSHOP bodyShopType from API:', bodyshopSubType);
                console.log('🚗 [Specific Data] Full bodyshop response:', response.data);

                // NK_BODYSHOP as bodyShopType is not a valid subtype — default to STANDARD
                const validSubtypesSet = ['STANDARD', 'ARS', 'RCI', 'NOBIS', 'AXA_DEUTSCHE_BANK'];
                if (!bodyshopSubType || !validSubtypesSet.includes(bodyshopSubType)) {
                  console.warn('⚠️ [Specific Data] bodyShopType "' + bodyshopSubType + '" is not a valid subtype, defaulting to STANDARD');
                  bodyshopSubType = 'STANDARD';
                }
                
                this.tipologiaCarrozzeria.set(bodyshopSubType);
                this.professionalsDataService.initializeProfessionalsData(
                  this.tipologia()!,
                  bodyshopSubType
                );
                
                console.log('✅ [Specific Data] BODYSHOP subtype loaded:', bodyshopSubType);
              } else {
                console.error('❌ [Specific Data] No data in bodyshop API response');
                this.messageService.add({
                  severity: 'error',
                  summary: 'Errore Configurazione',
                  detail: `Sottotipologia carrozzeria non disponibile`,
                  life: 8000,
                });
              }
            },
            error: (error) => {
              console.error('❌ [Specific Data] Error loading bodyshop data:', error);
              this.messageService.add({
                severity: 'error',
                summary: 'Errore',
                detail: 'Impossibile caricare i dati della carrozzeria',
                life: 5000,
              });
            }
          });
        }
      }
    }
    
    console.log('✅ [Specific Data] Tipologia loaded successfully:', this.tipologia());
  }

  handleSave(specificData: any) {
    try {
      this.validationErrorSections.set([]);
      // Add section status to specificData if not already present
      if (specificData.specificData && !specificData.specificData.specificDataSectionStatus) {
        specificData.specificData.specificDataSectionStatus = 'FILLED';
      }
      
      this.professionalsDataService.saveSpecificData(specificData);
      const allData = this.professionalsDataService.getAllData();
      console.log('Saving to API:', allData);
      
      // Update cache with new data and mark step as completed
      this.professionalsWizardService.updateSpecificDataCacheAfterSave(specificData);

      // Notify parent (e.g. NetworkBodyShopOnboardingComponent) of successful save
      this.dataSaved.emit();

      // In network body shop onboarding mode (forceEditable), don't navigate away
      if (this.forceEditable()) {
        return;
      }

      // In operational location (sede operativa) onboarding mode, don't navigate away
      if (this.professionalsWizardService.isOperationalLocationOnboarding()) {
        return;
      }

      // Navigate to next step (Questionari e Allegati)
      // For NK onboarding, always use the :id route to avoid unauthorized access for backoffice_professional role
      if (this.professionalsWizardService.isNetworkOnboarding()) {
        const bodyShopId = this.professionalsWizardService.networkOnboardingBodyShopId();
        this.bypassUnsavedChangesWarningForNextNavigation();
        this.router.navigate([`/axa/professionals-questionnaire-attachments/${bodyShopId}`]).then(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
        return;
      }

      // Navigate to next visible step (Impiegati Amministrativi e Collaboratori)
      const nextStep = this.professionalsWizardService.getNextVisibleStep(1);
      if (nextStep) {
        this.bypassUnsavedChangesWarningForNextNavigation();
        this.router.navigate([nextStep.routerLink]).then(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      }
    } catch (error) {
      console.error('Errore durante il salvataggio dei dati specifici:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile salvare i dati specifici. Riprova più tardi.',
        life: 5000,
      });
    }
  }

  handleSaveDraft(specificData: any) {
    try {
      this.validationErrorSections.set([]);
      // Add section status to specificData if not already present
      if (specificData.specificData && !specificData.specificData.specificDataSectionStatus) {
        specificData.specificData.specificDataSectionStatus = 'DRAFT';
      }
      
      this.professionalsDataService.saveSpecificData(specificData);
      console.log('Draft saved:', specificData);
      
      // Update cache with new data and mark step as NOT completed
      this.professionalsWizardService.updateSpecificDataCacheAfterSave(specificData);
      
      // TODO: Implement draft save to API
    } catch (error) {
      console.error('Errore durante il salvataggio della bozza:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile salvare la bozza. Riprova più tardi.',
        life: 5000,
      });
    }
  }

  skipToQuestionnaires(): void {
    this.professionalsWizardService.markStepCompleted(1, true);
    const nextStep = this.professionalsWizardService.steps().find(s => s.id === 2);
    if (nextStep) {
      this.bypassUnsavedChangesWarningForNextNavigation();
      this.router.navigate([nextStep.routerLink]).then(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }

  backToProgression(): void {
    this.router.navigate(['/axa/professionals-registration-progress']).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  /**
   * Sospendi la carrozzeria NK dal rapporto di network
   */
  onSuspendBodyShop(): void {
    const bodyShopId = this.route.snapshot.paramMap.get('id') ?? '';
    const name = this.professionalsWizardService.cachedGeneralData()?.businessName ?? bodyShopId;
    this.confirmationService.confirm({
      message: `Vuoi sospendere la carrozzeria <strong>${name}</strong>?`,
      header: 'Conferma Sospensione',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sospendi',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-warning',
      accept: () => {
        this.isSuspendingOrReactivating.set(true);
        this.professionalsService.updateBodyShopStatus(bodyShopId, 'SUSPEND').subscribe({
          next: (res) => {
            if (res.status) {
              this.bodyShopStatus.set('SUSPENDED');
              this.professionalsWizardService.networkBodyShopStatus.set('SUSPENDED');
              this.messageService.add({ severity: 'success', summary: 'Carrozzeria sospesa', detail: `${name} è stata sospesa.`, life: 4000 });
            }
            this.isSuspendingOrReactivating.set(false);
          },
          error: () => {
            this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Impossibile sospendere la carrozzeria.', life: 5000 });
            this.isSuspendingOrReactivating.set(false);
          },
        });
      },
    });
  }

  /**
   * Riattiva la carrozzeria NK sospesa
   */
  onReactivateBodyShop(): void {
    const bodyShopId = this.route.snapshot.paramMap.get('id') ?? '';
    const name = this.professionalsWizardService.cachedGeneralData()?.businessName ?? bodyShopId;
    this.confirmationService.confirm({
      message: `Vuoi riattivare la carrozzeria <strong>${name}</strong>?`,
      header: 'Conferma Riattivazione',
      icon: 'pi pi-info-circle',
      acceptLabel: 'Riattiva',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-success',
      accept: () => {
        this.isSuspendingOrReactivating.set(true);
        this.professionalsService.updateBodyShopStatus(bodyShopId, 'REACTIVATE').subscribe({
          next: (res) => {
            if (res.status) {
              this.bodyShopStatus.set('ACTIVE');
              this.professionalsWizardService.networkBodyShopStatus.set('ACTIVE');
              this.messageService.add({ severity: 'success', summary: 'Carrozzeria riattivata', detail: `${name} è stata riattivata.`, life: 4000 });
            }
            this.isSuspendingOrReactivating.set(false);
          },
          error: () => {
            this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Impossibile riattivare la carrozzeria.', life: 5000 });
            this.isSuspendingOrReactivating.set(false);
          },
        });
      },
    });
  }

  /**
   * Elimina (rimuove dal network) la carrozzeria NK
   */
  onDeleteBodyShop(): void {
    const bodyShopId = this.route.snapshot.paramMap.get('id') ?? '';
    const name = this.professionalsWizardService.cachedGeneralData()?.businessName ?? bodyShopId;
    const networkId = this.professionalsWizardService.networkOnboardingNetworkId();
    this.confirmationService.confirm({
      message: `Vuoi eliminare la carrozzeria <strong>${name}</strong> dal network? L'operazione non è reversibile.`,
      header: 'Conferma Eliminazione',
      icon: 'pi pi-trash',
      acceptLabel: 'Elimina',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.professionalsService.removeBodyShopFromNetwork(bodyShopId).subscribe({
          next: (res) => {
            if (res.status) {
              this.messageService.add({ severity: 'success', summary: 'Carrozzeria eliminata', detail: `${name} è stata rimossa dal network.`, life: 4000 });
              setTimeout(() => {
                if (networkId) {
                  this.router.navigate(['/axa/review-professional', networkId]);
                } else {
                  this.router.navigate(['/axa/dashboard']);
                }
              }, 1500);
            }
          },
          error: () => {
            this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Impossibile eliminare la carrozzeria.', life: 5000 });
          },
        });
      },
    });
  }
}
